import { useState } from 'preact/hooks'
import { Button } from '~/components/primitives/Button'
import { NumericField, TextField } from '~/components/primitives/Field'
import type { DomainGoalRecord, PlannedItem, WeeklyCommitment } from '~/models/fitnessDomain'
import { FITNESS_DOMAIN_LABELS } from '~/models/fitnessDomain'
import { setWeeklyCommitment } from '~/services/domainPlanService'
import { useDatabase } from '~/storage/DatabaseContext'
import type { CalendarDate } from '~/types/dates'
import { formatCalendarDate } from '~/utils/format'
import styles from './PlanPage.module.css'

/**
 * Editing what a week contains (D-099).
 *
 * **The three variants are descriptions of a week, not modes the user switches
 * between.** The app never asks which kind of week this is; it asks what a full
 * week, a short week and a bare-minimum week would look like, and then reports
 * what the week so far amounts to. Letting the user *declare* "this is a short
 * week" would turn a neutral description into a commitment they can then fail.
 *
 * **The minimum-viable version is required and cannot be skipped.** A goal with
 * no floor has no low-energy path, which is the whole reason the field exists —
 * and pre-filling it with a copy of the short week would make the guarantee true
 * and meaningless, so it is left empty and asked for.
 *
 * **The effective date is shown before the save, not after.** A change lands at
 * the start of next week, and a user who is not told that will read an unchanged
 * checklist as a bug.
 */

type VariantKey = keyof WeeklyCommitment

const VARIANTS: readonly { key: VariantKey; label: string; hint: string }[] = [
  { key: 'standard', label: 'A full week', hint: 'What you plan to do in an ordinary week.' },
  { key: 'short', label: 'A short week', hint: 'When time is tight but nothing has gone wrong.' },
  {
    key: 'minimumViable',
    label: 'A bare-minimum week',
    hint: 'The smallest version that still counts. This one is required.',
  },
]

/**
 * The editor's own working shape: strings, so what was typed is what is checked.
 *
 * `rowId` exists because these rows are removable from the middle. Keyed by
 * index, removing the first row would hand the second row's values to the first
 * row's inputs — the fields are controlled, so the text would be right, but the
 * focus and the `useId` associations would follow the wrong row.
 */
interface DraftItem {
  rowId: number
  label: string
  timesPerWeek: string
  minutes: string
}

let nextRowId = 0
const emptyItem = (): DraftItem => ({
  rowId: nextRowId++,
  label: '',
  timesPerWeek: '1',
  minutes: '',
})

type Draft = Record<VariantKey, DraftItem[]>

function toDraft(commitment: WeeklyCommitment | undefined): Draft {
  const read = (items: readonly PlannedItem[] | undefined): DraftItem[] =>
    (items ?? []).map((item) => ({
      rowId: nextRowId++,
      label: item.label,
      timesPerWeek: String(item.timesPerWeek),
      minutes: item.minutes === undefined ? '' : String(item.minutes),
    }))

  return {
    standard: read(commitment?.standard),
    short: read(commitment?.short),
    minimumViable: read(commitment?.minimumViable),
  }
}

function toPlannedItems(items: readonly DraftItem[]): readonly PlannedItem[] {
  return items
    .filter((item) => item.label.trim().length > 0)
    .map((item) => ({
      label: item.label.trim(),
      timesPerWeek: Number(item.timesPerWeek),
      ...(item.minutes.trim() === '' ? {} : { minutes: Number(item.minutes) }),
    }))
}

interface CommitmentEditorProps {
  goal: DomainGoalRecord
  effectiveFrom: CalendarDate | undefined
  onSaved: () => void
  onCancel: () => void
  /**
   * Values proposed by a second opinion, to start from instead of the stored
   * plan (D-093 §6).
   *
   * **Present means the editor is showing someone else's numbers**, so the screen
   * says so and the saved change is tagged `geminiProposal` in the goal's history.
   * Absent is the ordinary case and nothing about it changes.
   *
   * It is a starting point for a form the user still has to submit. Nothing is
   * written by arriving here.
   */
  prefill?: WeeklyCommitment | undefined
}

export function CommitmentEditor({
  goal,
  effectiveFrom,
  onSaved,
  onCancel,
  prefill,
}: CommitmentEditorProps) {
  const db = useDatabase()
  const [draft, setDraft] = useState<Draft>(() => toDraft(prefill ?? goal.weeklyCommitment))
  const [error, setError] = useState<string | undefined>(undefined)
  const [busy, setBusy] = useState(false)

  const update = (key: VariantKey, index: number, field: keyof DraftItem, value: string): void => {
    setDraft((current) => ({
      ...current,
      [key]: current[key].map((item, at) => (at === index ? { ...item, [field]: value } : item)),
    }))
  }

  const add = (key: VariantKey): void => {
    setDraft((current) => ({ ...current, [key]: [...current[key], emptyItem()] }))
  }

  const remove = (key: VariantKey, index: number): void => {
    setDraft((current) => ({ ...current, [key]: current[key].filter((_, at) => at !== index) }))
  }

  const save = async (): Promise<void> => {
    setBusy(true)
    setError(undefined)

    const commitment: WeeklyCommitment = {
      standard: toPlannedItems(draft.standard),
      short: toPlannedItems(draft.short),
      minimumViable: toPlannedItems(draft.minimumViable),
    }

    /* One write path, whoever proposed the numbers (D-093 §6). `source` is a
       label on the history entry, not a different door — two write paths with
       only one accountable is how a change reaches a record with nothing
       appended to its history. */
    const saved = await setWeeklyCommitment(
      db,
      goal.id,
      commitment,
      new Date(),
      undefined,
      prefill === undefined ? undefined : 'geminiProposal',
    )
    setBusy(false)

    if (!saved.ok) {
      setError(saved.error.message)
      return
    }
    onSaved()
  }

  return (
    <div class={styles.editor}>
      {/* Deliberately not "Change the weekly plan", which the plan for this
          checkpoint proposed. Playwright matches an accessible name by
          substring, and that string contains the "Weekly plan" heading each
          non-editing area renders — with two areas on screen, one editing and
          one not, a lookup for either would resolve to two elements. The button
          that opens this editor may keep the phrase, because it is a button and
          the collision is between headings. */}
      <h3 class={styles.editorHeading}>
        Change what a week contains — {FITNESS_DOMAIN_LABELS[goal.domain]}
      </h3>

      {/* Said before the fields, not after: someone who scrolls straight to Save
          must already know whose numbers these are. It is also why the values are
          editable rather than presented as an accept/reject — a suggestion the
          user cannot change is a suggestion they either take whole or lose. */}
      {prefill === undefined ? null : (
        <p class={styles.body}>
          <strong>These numbers came from your second opinion.</strong> Nothing has been saved.
          Change anything you disagree with, then save it yourself.
        </p>
      )}

      <p class={styles.body}>
        {effectiveFrom === undefined
          ? 'This takes effect from the start of next week.'
          : `This takes effect from ${formatCalendarDate(effectiveFrom)}. The week you are in now keeps the plan it started with.`}
      </p>

      {VARIANTS.map((variant) => (
        <fieldset key={variant.key} class={styles.variant}>
          <legend class={styles.variantLegend}>{variant.label}</legend>
          <p class={styles.goalNote}>{variant.hint}</p>

          {draft[variant.key].map((item, index) => (
            <div key={item.rowId} class={styles.itemRow}>
              <TextField
                label="What"
                value={item.label}
                onInput={(value) => update(variant.key, index, 'label', value)}
                placeholder="Easy run"
              />
              <NumericField
                label="Times a week"
                value={item.timesPerWeek}
                onInput={(value) => update(variant.key, index, 'timesPerWeek', value)}
              />
              <NumericField
                label="Minutes each"
                value={item.minutes}
                onInput={(value) => update(variant.key, index, 'minutes', value)}
                hint="Optional."
              />
              <Button
                variant="secondary"
                onClick={() => remove(variant.key, index)}
                label={`Remove ${item.label === '' ? 'this item' : item.label} from ${variant.label.toLowerCase()}`}
              >
                Remove
              </Button>
            </div>
          ))}

          <Button
            variant="secondary"
            onClick={() => add(variant.key)}
            label={`Add something to ${variant.label.toLowerCase()}`}
          >
            Add something
          </Button>
        </fieldset>
      ))}

      {error === undefined ? null : (
        <p class={styles.error} role="alert">
          {error}
        </p>
      )}

      <div class={styles.actions}>
        <Button onClick={() => void save()} busy={busy}>
          Save the weekly plan
        </Button>
        <Button variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  )
}
